/** MIME types the agent accepts as inline base64 images. */
export const AGENT_IMAGE_MIME_TYPES = new Set(['image/jpeg', 'image/png']);

/**
 * Resize and JPEG-compress an image File into a base64 payload for the `image-upload`
 * message the agent receives inline.
 *
 * The longest edge is capped at `maxDimension` (default 512px) and the output is always
 * JPEG at 0.7 quality — enough for the model to recognise content without bloating the
 * request. The full-quality asset is stored separately via the uploader (MCP), so this
 * lossy copy is only ever the model's view, never the customer's.
 */
export async function compressImage(
    file: File,
    maxDimension = 512
): Promise<{ base64: string; mediaType: 'image/jpeg' }> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(objectUrl);

            let { naturalWidth: width, naturalHeight: height } = img;

            if (width > height && width > maxDimension) {
                height = Math.round((height * maxDimension) / width);
                width = maxDimension;
            } else if (height > maxDimension) {
                width = Math.round((width * maxDimension) / height);
                height = maxDimension;
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            canvas.getContext('2d')?.drawImage(img, 0, 0, width, height);

            const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
            const base64 = dataUrl.replace(/^data:image\/jpeg;base64,/, '');
            resolve({ base64, mediaType: 'image/jpeg' });
        };

        img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('Failed to load image for compression'));
        };

        img.src = objectUrl;
    });
}

/**
 * Reads an image's longest edge (in pixels) off-DOM. Used only to drive the low-res
 * warning, so failure is the caller's cue to skip the warning, not an error to surface.
 */
export function measureLongestEdge(file: File): Promise<number> {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(Math.max(img.naturalWidth, img.naturalHeight));
        };
        img.onerror = (event) => {
            URL.revokeObjectURL(url);
            reject(event);
        };
        img.src = url;
    });
}
